import { SlideOverContentWrapper, SlideOverFooter, SlideOverWrapper } from '@/components/slideOvers_v2';
import { Loadable } from '@/components/loaders';
import { DataSourceTypePanelHeaderV2 } from '@/features/dataSources/components';
import { DataSourceStatusBadge } from '@/features/dataSources/components/commons/DataSourceStatusBadge';
import { SourceConnectorDisconnectModal } from '@/features/dataSources/components/sourceConnectors/SourceConnectorDisconnectModal';
import { useSourceConnectorDisconnect } from '@/features/dataSources/components/sourceConnectors/useSourceConnectorDisconnect';
import { useSourceConnectorLinkedDataSources } from '@/features/dataSources/components/sourceConnectors/useSourceConnectorLinkedDataSources';
import { SourceConnectorCatalogItem } from '@/features/dataSources/components/sourceConnectors/useSourceConnectorCatalogItems';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSource, DataSourceIDEnum } from '@/models/dataSource';
import { ISlideOverContentProps } from '@/models/slideOver';
import { ChevronRightIcon, DatabaseIcon, LightningBoltIcon, PlusIcon } from '@heroicons/react/outline';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { twMerge } from 'tailwind-merge';

type SourceConnectorManageSlideOverProps = ISlideOverContentProps & {
  item: SourceConnectorCatalogItem;
  onAddDataSource: () => void;
};

const ConnectionActiveBadge: FC = () => {
  const { t } = useTranslation('data_sources');

  return (
    <span className='inline-flex items-center gap-2 rounded-full bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700'>
      <span className='size-2 rounded-full bg-green-500' />
      {t('source_connectors.active_status')}
    </span>
  );
};

const SectionTitle: FC<{ icon: FC<{ className?: string }>; label: string }> = ({ icon: Icon, label }) => (
  <div className='flex items-center gap-2'>
    <Icon className='size-5 text-brand-600' />
    <h3 className='text-sm font-semibold text-brand-600'>{label}</h3>
  </div>
);

type LinkedDataSourceRowProps = {
  dataSource: DataSource;
  onClick: (dataSource: DataSource) => void;
};

const LinkedDataSourceRow: FC<LinkedDataSourceRowProps> = ({ dataSource, onClick }) => (
  <button
    type='button'
    onClick={() => onClick(dataSource)}
    className='flex w-full items-center justify-between gap-3 rounded-xl border border-quaternary-300 bg-white-100 px-4 py-3 text-left transition hover:border-brand-200'
  >
    <span className='text-sm font-medium text-gray-900'>{dataSource.name}</span>
    <div className='flex items-center gap-3'>
      <DataSourceStatusBadge status={dataSource.status} />
      <ChevronRightIcon className='size-5 shrink-0 text-quaternary-400' />
    </div>
  </button>
);

export const SourceConnectorManageSlideOver: FC<SourceConnectorManageSlideOverProps> = ({ item, closeSlideOver, onAddDataSource }) => {
  const { t } = useTranslation('data_sources');
  const history = useHistory();
  const { id: workspaceId } = useWorkspace();
  const { connector, dataSourceType, isPlaceholder } = item;
  const typeId = dataSourceType.id as DataSourceIDEnum;

  const [isDisconnectModalOpen, setIsDisconnectModalOpen] = useState(false);
  const [disconnectError, setDisconnectError] = useState<string | null>(null);

  const { linkedDataSources, isLoading } = useSourceConnectorLinkedDataSources({ workspaceId, item });
  const { disconnect, isDisconnecting, canDisconnect } = useSourceConnectorDisconnect({ workspaceId, connector });

  const handleDataSourceClick = (dataSource: DataSource) => {
    if (dataSource.id.startsWith('mock-')) return;

    closeSlideOver();
    history.push(`/data-sources/${dataSource.id}`);
  };

  const openDisconnectModal = () => {
    setDisconnectError(null);
    setIsDisconnectModalOpen(true);
  };

  const handleDisconnectConfirm = async (force: boolean) => {
    const result = await disconnect(force);
    if (!result.success) {
      setDisconnectError(result.errorMessage ?? null);
      return;
    }

    setDisconnectError(null);
    setIsDisconnectModalOpen(false);
    closeSlideOver();
  };

  return (
    <SlideOverWrapper>
      <DataSourceTypePanelHeaderV2
        id={typeId}
        title={t(`catalog.${typeId}.title`, { defaultValue: dataSourceType.name })}
        subtitle={<ConnectionActiveBadge />}
      />

      <SlideOverContentWrapper className='!mt-0 gap-8 px-4'>
        <section className='flex flex-col gap-3'>
          <SectionTitle icon={DatabaseIcon} label={t('source_connectors.manage_data_sources_title')} />

          <Loadable isLoading={isLoading}>
            {linkedDataSources.length > 0 ? (
              <div className='flex flex-col gap-2'>
                {linkedDataSources.map((dataSource) => (
                  <LinkedDataSourceRow key={dataSource.id} dataSource={dataSource} onClick={handleDataSourceClick} />
                ))}
              </div>
            ) : (
              <p className='text-sm text-secondary-600'>{t('source_connectors.no_data_sources')}</p>
            )}
          </Loadable>
        </section>

        <section className='flex flex-col gap-2'>
          <SectionTitle icon={LightningBoltIcon} label={t('source_connectors.connection_section_title')} />

          <button
            type='button'
            className={twMerge(
              'w-fit rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white-100 hover:bg-red-700',
              (!canDisconnect || isPlaceholder) && 'pointer-events-none opacity-50'
            )}
            disabled={!canDisconnect || isPlaceholder}
            onClick={openDisconnectModal}
          >
            {t('source_connectors.disconnect')}
          </button>

          <p className='text-sm text-secondary-500'>{t('source_connectors.disconnect_description')}</p>
        </section>
      </SlideOverContentWrapper>

      <SlideOverFooter
        closeSlideOver={closeSlideOver}
        confirmLabel={t('source_connectors.add_data_source')}
        showCancelButton={false}
        onConfirmClicked={onAddDataSource}
        confirmIconRight={<PlusIcon />}
      />

      {isDisconnectModalOpen && (
        <SourceConnectorDisconnectModal
          connectorName={connector.name}
          isDisconnecting={isDisconnecting}
          errorMessage={disconnectError}
          onConfirm={handleDisconnectConfirm}
          onClose={() => {
            setDisconnectError(null);
            setIsDisconnectModalOpen(false);
          }}
        />
      )}
    </SlideOverWrapper>
  );
};
